<template>
  <v-sheet class="border-opacity-25 border-thin rounded-lg pa-5">
    <v-form ref="form" @submit.prevent="onSubmit">
      <v-row>
        <v-col cols="12" md="12">
          <v-text-field v-model="criteriaGroup.group_name" label="Name" :rules="[rules.required]" />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="12"
          >Description
          <Editor
            v-model="criteriaGroup.description"
            :init="{
              base_url: '/tinymce',
              suffix: '.min',
              plugins: 'lists link image table code',
              toolbar:
                'undo redo | blocks | bold italic | alignleft aligncenter alignright | bullist numlist | link image table | code',
              skin: 'oxide',
              content_css: 'default',
            }"
            licenseKey="gpl" />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="12">
          <v-select
            v-model="criteriaGroup.color"
            label="Color"
            :items="basicColors"
            item-value="value"
            item-title="label" />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-btn
            :loading="isSubmitting"
            color="secondary"
            block
            class="rounded-lg mb-8"
            elevation="2"
            @click="emit('criteriaGroupEditCanceled')">
            Cancel
          </v-btn>
        </v-col>
        <v-col>
          <v-btn
            type="submit"
            :loading="isSubmitting"
            block
            class="rounded-lg mb-8"
            elevation="2"
            color="primary"
            >{{ saveLabel }}</v-btn
          >
        </v-col>
      </v-row>
    </v-form>
  </v-sheet>
</template>
<script lang="ts" setup>
import { CriteriaGroupDTO, ICriteriaGroupDTO } from '@/dtos';
import { computed, ref, toRef, watch } from 'vue';
import type { VForm } from 'vuetify/components';
import { rules, basicColors } from '@/helpers';
import { useCriteriaStore, useUpdateStatusStore } from '@/stores/modules';
import { SNACKSTATUS } from '@/stores/util';
import Editor from '@tinymce/tinymce-vue';
import '@/components/tinymce/tinymce';
interface Props {
  criteriaGroup: ICriteriaGroupDTO;
  yearId: string;
  isCreate: boolean;
}
const criteriaStore = useCriteriaStore();
const statusStore = useUpdateStatusStore();
const props = defineProps<Props>();
const emit = defineEmits(['criteriaGroupEditComplete', 'criteriaGroupEditCanceled', 'snack']);

const criteriaGroup = ref<ICriteriaGroupDTO>(new CriteriaGroupDTO(props.criteriaGroup));
const isCreate = toRef(props, 'isCreate', false);
const yearId = toRef(props, 'yearId', '');
const form = ref<InstanceType<typeof VForm> | null>();
const isSubmitting = ref<boolean>(false);

const saveLabel = computed(() => {
  if (isCreate.value == true) {
    return 'Create Criteria Group';
  } else {
    return 'Save';
  }
});

const onSubmit = async () => {
  if (!form.value) {
    return;
  }
  isSubmitting.value = true;
  if (isCreate.value) {
    criteriaStore.createGroupCriteria.mutateAsync({
      yearId: yearId.value,
      criteriaGroup: criteriaGroup.value,
    });
  } else {
    criteriaStore.updateGroupCriteria.mutateAsync(criteriaGroup.value);
  }
};
const finalizeSubmission = () => {
  isSubmitting.value = false;
  emit('criteriaGroupEditComplete');
};

watch(
  () => statusStore.getStatus(`criteriaGroup-${criteriaGroup.value.id}`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `criteriaGroup-${criteriaGroup.value.id}`;
    emit('snack', 'Criteria Group', value, statusStore.getError(key));
    finalizeSubmission();
  },
);

watch(
  () => statusStore.getStatus(`criteriaGroup-create`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `criteriaGroup-create`;
    emit('snack', 'Criteria Group', value, statusStore.getError(key));
    finalizeSubmission();
  },
);
</script>
